import type { GameState } from "./types";

const SAVE_KEY = "wildmere.save.v1";
const HISTORY_KEY = "wildmere.history.v1";

export interface RunHistoryEntry {
  seed: string;
  playerName: string;
  classId: string;
  charterId?: string;
  year: number;
  season: string;
  day: number;
  mainCompleted: number;
  sideCompleted: number;
  playStats: GameState["gameStats"];
  savedAt: string;
}

export function saveGame(state: GameState): void {
  try { localStorage.setItem(SAVE_KEY, JSON.stringify(state)); } catch { /* storage is optional */ }
}

export function loadGame(): GameState | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw) as GameState;
    return state.version === 1 ? state : null;
  } catch { return null; }
}

export function clearGame(): void {
  try { localStorage.removeItem(SAVE_KEY); } catch { /* ignored */ }
}

export function exportGame(state: GameState): string {
  return JSON.stringify(state, null, 2);
}

export function importGame(serialized: string): GameState {
  const parsed = JSON.parse(serialized) as GameState;
  if (parsed.version !== 1 || !parsed.seed || !parsed.classId || !parsed.skills || !parsed.inventory) throw new Error("This is not a compatible Wildmere save.");
  return parsed;
}

export function saveHistory(state: GameState): void {
  try {
    const history = loadHistory();
    history.unshift({
      seed: state.seed,
      playerName: state.playerName,
      classId: state.classId,
      charterId: state.charterId,
      year: state.year,
      season: state.season,
      day: state.day,
      mainCompleted: Object.entries(state.quests).filter(([id, quest]) => id.startsWith("main-") && quest.status === "complete").length,
      sideCompleted: Object.entries(state.quests).filter(([id, quest]) => id.startsWith("side-") && quest.status === "complete").length,
      playStats: state.gameStats,
      savedAt: new Date().toISOString(),
    });
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 12)));
  } catch { /* ignored */ }
}

export function loadHistory(): RunHistoryEntry[] {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]") as RunHistoryEntry[]; } catch { return []; }
}
